import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import notification from 'antd/lib/notification';

import { CombinedState } from 'reducers';
import { saveAnnotationsAsync } from 'actions/annotation-actions';
import { getCore } from 'cvat-core-wrapper';
import { t } from 'utils/i18n';

// the image that was on `current` has the number of its new place
export function frameAfterReorder(order: number[], current: number): number {
    const index = order.indexOf(current);
    return index < 0 ? current : [...order].sort((a, b) => a - b)[index];
}

// Saves the order and loads the page again on the same image: the frames keep their numbers, the images,
// names and annotations under them are other ones, so nothing loaded before can be reused. A rejection
// leaves the page as it was.
export function useSaveFrameOrder(): (dataPath: string, order: number[]) => Promise<void> {
    const dispatch = useDispatch();
    const job = useSelector((state: CombinedState) => state.annotation.job.instance);
    const current = useSelector((state: CombinedState) => state.annotation.player.frame.number);

    return useCallback(async (dataPath, order) => {
        // the annotations of the open job are numbered by the old order
        if (job?.annotations.hasUnsavedChanges()) {
            await dispatch(saveAnnotationsAsync() as any);
            if (job.annotations.hasUnsavedChanges()) {
                notification.error({
                    message: t('Your changes could not be saved'), description: t('Press Ctrl+S to try again.'),
                });
                throw new Error('unsaved changes');
            }
        }

        const core = getCore();
        try {
            await core.server.request(`${core.config.backendAPI}${dataPath}/order`, {
                method: 'POST', data: { frames: order },
            });
        } catch (error: unknown) {
            notification.error({
                message: t('Could not save the order'),
                description: error instanceof Error ? error.message : String(error),
            });
            throw error;
        }

        const { pathname, search, hash } = window.location;
        const params = new URLSearchParams(search);
        params.set('frame', String(frameAfterReorder(order, current)));
        window.location.assign(`${pathname}?${params}${hash}`);
    }, [dispatch, job, current]);
}
